// @sms/sdk — typed REST client for the SMS Nepal API.
//
// The Client carries:
//   - baseURL: the API origin (no trailing slash).
//   - fetchImpl: injectable for tests; defaults to globalThis.fetch.
//   - cookieJar: optional; when set, the client reads CSRF cookies from
//     it rather than document.cookie (used in Node tests).
//
// Mutating requests (POST/PUT/PATCH/DELETE) carry two extra headers:
//
//   - X-CSRF-Token: copied from the `sms_csrf` cookie set by the auth
//     middleware; required by the CSRF middleware on the API.
//   - Idempotency-Key: a fresh UUIDv4 per call so the worker can dedupe
//     replays on flaky mobile networks (matches the API's idempotent
//     capture pattern for arrivals + attendance).
//
// Error mapping happens once, in handle(), so call-site code can branch
// on instanceof without parsing problem bodies.

import {
  ApiError,
  ConflictError,
  type ProblemDetails,
  RateLimitError,
  TOTPRequiredError,
  UnauthorizedError,
  ValidationError,
} from "./errors";
import type {
  ArrivalToday,
  AuditTrailResponse,
  ComposeNoticeRequest,
  DevicePairResponse,
  InboxNotice,
  Me,
  MonthlyAttendanceSummary,
  NoticeReach,
  NoticeRecipientsPage,
  OTPKind,
  PendingOTP,
  PushSubscriptionAck,
  PushSubscriptionRegister,
  RecordArrivalRequest,
  RecordArrivalResponse,
  RosterImportReport,
  Student,
  SubmitAttendanceRequest,
  SubmitAttendanceResponse,
  VAPIDPublicKeyResponse,
  Ward,
} from "./types";

export * from "./errors";
export * from "./types";

export interface ClientOptions {
  baseURL: string;
  fetchImpl?: typeof fetch;
  /**
   * Override the cookie source. When omitted, the client uses
   * document.cookie (browser). Tests pass a Map-backed jar so they can
   * assert CSRF threading without a DOM.
   */
  cookieJar?: CookieJar;
}

export interface CookieJar {
  get(name: string): string | undefined;
}

/** Default jar: reads document.cookie when available, otherwise empty. */
const browserJar: CookieJar = {
  get(name: string): string | undefined {
    if (typeof document === "undefined") return undefined;
    const prefix = name + "=";
    const all = document.cookie.split("; ");
    for (const entry of all) {
      if (entry.startsWith(prefix)) return entry.slice(prefix.length);
    }
    return undefined;
  },
};

export class Client {
  private readonly baseURL: string;
  private readonly fetchImpl: typeof fetch;
  private readonly cookieJar: CookieJar;

  constructor(opts: ClientOptions) {
    this.baseURL = opts.baseURL.replace(/\/$/, "");
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.cookieJar = opts.cookieJar ?? browserJar;
  }

  // ── auth ─────────────────────────────────────────────────────────

  async requestGuardianOTP(phone: string): Promise<void> {
    await this.post("/auth/otp/request", { phone, kind: "guardian" satisfies OTPKind });
  }

  async verifyGuardianOTP(phone: string, otp: string): Promise<Me> {
    return this.post<Me>("/auth/otp/verify", { phone, otp });
  }

  async loginStaff(args: {
    email: string;
    password: string;
    schoolSlug?: string;
    totpCode?: string;
  }): Promise<Me> {
    const body: Record<string, unknown> = {
      email: args.email,
      password: args.password,
    };
    if (args.schoolSlug) body.school_slug = args.schoolSlug;
    if (args.totpCode) body.totp_code = args.totpCode;
    return this.post<Me>("/auth/login", body);
  }

  async logout(): Promise<void> {
    await this.post("/auth/logout", {});
  }

  async me(): Promise<Me> {
    return this.get<Me>("/me");
  }

  /**
   * DELETE /api/v1/guardians/consent — revokes the calling guardian's
   * notification consent. The server clears `guardians.consent_at`;
   * subsequent notice fan-outs skip this guardian. Idempotent.
   */
  async revokeConsent(): Promise<void> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const csrf = this.cookieJar.get("sms_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await this.fetchImpl(`${this.baseURL}/api/v1/guardians/consent`, {
      method: "DELETE",
      credentials: "include",
      headers,
    });
    if (res.ok) return;
    let problem: ProblemDetails = { status: res.status, title: res.statusText };
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("json")) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* fall through */
      }
    }
    throw mapError(res, problem);
  }

  // ── arrivals ─────────────────────────────────────────────────────

  async todayArrivals(): Promise<ArrivalToday[]> {
    // Go API wraps the list in {arrivals: [...], count, date}; older
    // test stubs return a bare array. Accept both.
    const res = await this.get<
      ArrivalToday[] | { arrivals: ArrivalToday[] }
    >("/schools/today/arrivals");
    if (Array.isArray(res)) return res;
    return res.arrivals ?? [];
  }

  // ── guardian ─────────────────────────────────────────────────────

  async myWards(): Promise<Ward[]> {
    // Same envelope-vs-bare-array pattern as noticeInbox/todayArrivals.
    const res = await this.get<Ward[] | { wards: Ward[] }>("/me/wards");
    if (Array.isArray(res)) return res;
    return res.wards ?? [];
  }

  /**
   * POST /api/v1/devices/pair — admin issues a one-time 6-char pair code.
   * The kiosk device redeems it via `claimDevicePair` (no auth, just the
   * code) and gets back its long-lived api_token. Code expires in
   * ~15 minutes.
   */
  async issueDevicePair(opts: {
    kind: "gate" | "teacher" | "admin";
    label: string;
    gateId?: string;
  }): Promise<{ code: string; expiresInSeconds: number }> {
    const res = await this.post<{ code: string; expires_in_seconds: number }>(
      "/devices/pair",
      {
        kind: opts.kind,
        label: opts.label,
        ...(opts.gateId ? { gate_id: opts.gateId } : {}),
      },
    );
    return { code: res.code, expiresInSeconds: res.expires_in_seconds };
  }

  /**
   * GET /api/v1/students/qr-sheet — admin-only. Returns a PDF blob the
   * caller can stream into an <a download> link. `sectionId` filters to
   * one section; omitting it returns the whole school's QR sheet.
   */
  async downloadQRSheet(opts: { sectionId?: string } = {}): Promise<Blob> {
    const qs = opts.sectionId
      ? `?section_id=${encodeURIComponent(opts.sectionId)}`
      : "";
    const res = await this.fetchImpl(
      `${this.baseURL}/api/v1/students/qr-sheet${qs}`,
      { credentials: "include", headers: { Accept: "application/pdf" } },
    );
    if (!res.ok) {
      const problem = await res.json().catch(() => null);
      throw mapError(res, problem);
    }
    return res.blob();
  }

  async noticeInbox(): Promise<InboxNotice[]> {
    const res = await this.get<InboxNotice[] | { notices: InboxNotice[] }>("/notices");
    // The Go API wraps the list in `{notices: [...]}`; older test
    // stubs return a bare array. Accept both.
    if (Array.isArray(res)) return res;
    return res.notices ?? [];
  }

  /**
   * GET /api/v1/notices/{id} — single-notice detail, resolved to the
   * guardian's locale by the server.
   */
  async getNotice(noticeId: string): Promise<InboxNotice> {
    return this.get<InboxNotice>(`/notices/${encodeURIComponent(noticeId)}`);
  }

  /**
   * POST /api/v1/notices/{id}/read — flips the guardian's in_app
   * delivery row to state='read'. Idempotent.
   */
  async markNoticeRead(noticeId: string): Promise<void> {
    await this.post<void>(`/notices/${encodeURIComponent(noticeId)}/read`, {});
  }

  // ── notices (admin) ──────────────────────────────────────────────

  async composeNotice(req: ComposeNoticeRequest): Promise<{ notice_id: string }> {
    return this.post<{ notice_id: string }>("/notices", req);
  }

  /**
   * GET /api/v1/audit/{kind}/{id}?limit=N — admin-only audit trail
   * for a single entity. Returns newest-first; `before`/`after` are
   * JSONB blobs passed through as open maps.
   */
  async getAuditTrail(
    entityKind: string,
    entityId: string,
    opts: { limit?: number } = {},
  ): Promise<AuditTrailResponse> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return this.get<AuditTrailResponse>(
      `/audit/${encodeURIComponent(entityKind)}/${encodeURIComponent(entityId)}${qs ? `?${qs}` : ""}`,
    );
  }

  /**
   * GET /api/v1/notices/{id}/reach — admin-only delivery report.
   * Returns total recipients, per-channel + per-state counts, and the
   * SMS cost rolled up across deliveries.
   */
  async getNoticeReach(noticeId: string): Promise<NoticeReach> {
    return this.get<NoticeReach>(`/notices/${encodeURIComponent(noticeId)}/reach`);
  }

  /**
   * GET /api/v1/notices/{id}/recipients — paginated reach drilldown.
   *
   *   - state: filter to one delivery state (queued|sent|delivered|
   *     failed|read). Omit for all rows.
   *   - limit: 1..500, default 100.
   *   - cursor: state_changed_at from a previous page's next_cursor.
   *
   * Phone numbers come back masked (server-side, in SQL). Admin only —
   * guardians get 403.
   */
  async getNoticeRecipients(
    noticeId: string,
    opts: { state?: string; limit?: number; cursor?: string } = {},
  ): Promise<NoticeRecipientsPage> {
    const params = new URLSearchParams();
    if (opts.state) params.set("state", opts.state);
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts.cursor) params.set("cursor", opts.cursor);
    const qs = params.toString();
    const suffix = qs ? `?${qs}` : "";
    return this.get<NoticeRecipientsPage>(
      `/notices/${encodeURIComponent(noticeId)}/recipients${suffix}`,
    );
  }

  /**
   * GET /api/v1/admin/otp/pending — pilot-mode admin lookup.
   *
   * Returns OTPs currently in apiserver memory for guardians whose
   * students belong to the caller's school. Designed for the "admin
   * reads the code back to the parent verbally" workflow before SMS
   * is wired. Each call is audit-logged per guardian revealed.
   *
   * Returns [] in prod (the apiserver doesn't keep an OTP cache
   * there) and in any environment where SMS is wired and parents
   * receive their codes directly.
   */
  async getAdminPendingOTPs(): Promise<PendingOTP[]> {
    return this.get<PendingOTP[]>("/admin/otp/pending");
  }

  // ── classroom / teacher ──────────────────────────────────────────

  /**
   * GET /api/v1/students. Returns the school's roster (RLS-scoped on
   * the server). Optionally narrows by `sectionId`; the v0 classroom
   * MFE lets the teacher filter client-side.
   */
  async listStudents(opts: { sectionId?: string; limit?: number } = {}): Promise<Student[]> {
    const params = new URLSearchParams();
    if (opts.sectionId) params.set("section_id", opts.sectionId);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const res = await this.get<{ students: Student[] }>(
      `/students${qs ? `?${qs}` : ""}`,
    );
    return res.students;
  }

  /**
   * POST /api/v1/students/import — multipart CSV upload.
   *
   * Two-phase by convention: the admin-app first calls this with
   * `dryRun: true`, renders the preview (valid/errors/sections), then
   * calls again without `dryRun` to commit. The Go importer runs
   * partial-commit semantics — valid rows are persisted even if some
   * rows in the same file failed parse/validation; the response
   * carries both the errors and the *_created/_updated counters so
   * the operator can fix the bad rows and re-upload.
   *
   * The 20 MB multipart cap on the server is the practical ceiling.
   */
  async importRoster(
    file: Blob,
    opts: { dryRun?: boolean; academicYear?: number } = {},
  ): Promise<RosterImportReport> {
    const params = new URLSearchParams();
    if (opts.dryRun) params.set("dry_run", "true");
    if (opts.academicYear !== undefined) {
      params.set("academic_year", String(opts.academicYear));
    }
    const qs = params.toString();

    const form = new FormData();
    form.append("file", file);

    const headers: Record<string, string> = {
      Accept: "application/json",
      "Idempotency-Key": uuidv4(),
    };
    // multipart Content-Type is set automatically by fetch (with the
    // correct boundary) when `body: FormData`; we MUST NOT set it
    // ourselves or the boundary parameter goes missing and the
    // server's multipart parser fails.
    const csrf = this.cookieJar.get("sms_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;

    const res = await this.fetchImpl(
      `${this.baseURL}/api/v1/students/import${qs ? `?${qs}` : ""}`,
      {
        method: "POST",
        credentials: "include",
        headers,
        body: form,
      },
    );
    if (res.ok) {
      return (await res.json()) as RosterImportReport;
    }
    let problem: ProblemDetails = { status: res.status, title: res.statusText };
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("json")) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* fall through */
      }
    }
    throw mapError(res, problem);
  }

  /** POST /api/v1/attendance/bulk — submits a day's worth of records. */
  async submitAttendance(req: SubmitAttendanceRequest): Promise<SubmitAttendanceResponse> {
    return this.post<SubmitAttendanceResponse>("/attendance/bulk", req);
  }

  /**
   * GET /api/v1/students/{id}/attendance?year=&month= — monthly
   * attendance summary for a single student. Year/month default to
   * the server's "now" when omitted; the response echoes the resolved
   * values either way so the UI can display them without recomputing.
   */
  async getMonthlyAttendance(
    studentId: string,
    opts: { year?: number; month?: number } = {},
  ): Promise<MonthlyAttendanceSummary> {
    const params = new URLSearchParams();
    if (opts.year !== undefined) params.set("year", String(opts.year));
    if (opts.month !== undefined) params.set("month", String(opts.month));
    const qs = params.toString();
    return this.get<MonthlyAttendanceSummary>(
      `/students/${encodeURIComponent(studentId)}/attendance${qs ? `?${qs}` : ""}`,
    );
  }

  // ── device pairing (gate kiosk) ──────────────────────────────────

  /**
   * POST /api/v1/devices/claim — exchanges a 6-character pairing code
   * for a long-lived device api_token. Unauthenticated; the caller is
   * a fresh tablet at the school entrance with no session yet. The
   * response's api_token is shown ONCE — persist it (gate-app stores
   * in localStorage) and feed it to DeviceClient for subsequent
   * arrival captures.
   */
  async claimDevicePair(code: string): Promise<DevicePairResponse> {
    return this.post<DevicePairResponse>("/devices/claim", { code });
  }

  // ── push notifications (Phase 2 of NOTICE_DELIVERY_PLAN) ─────────

  /**
   * GET /api/v1/push/vapid-public-key — unauthenticated. Returns the
   * base64url-encoded VAPID public key the browser feeds to
   * `PushManager.subscribe({ applicationServerKey: ... })`. Throws
   * `ApiError` with status 503 when push isn't configured on this
   * deployment (parent-app should surface "push unavailable" and
   * hide the Enable toggle).
   */
  async getVAPIDPublicKey(): Promise<string> {
    const wrap = await this.get<VAPIDPublicKeyResponse>("/push/vapid-public-key");
    return wrap.public_key;
  }

  /**
   * POST /api/v1/me/push-subscription — guardian only. Pass the
   * `subscription.toJSON()` shape directly from the browser; the
   * server stores endpoint + keys and idempotently upserts by
   * endpoint, so re-subscribing returns the existing id.
   */
  async subscribePushNotification(
    sub: PushSubscriptionRegister,
  ): Promise<PushSubscriptionAck> {
    return this.post<PushSubscriptionAck>("/me/push-subscription", sub);
  }

  /**
   * DELETE /api/v1/me/push-subscription — guardian only. Idempotent;
   * a missing row still returns 204. The local PushManager's own
   * `subscription.unsubscribe()` is the caller's responsibility.
   */
  async unsubscribePushNotification(endpoint: string): Promise<void> {
    await this.delete("/me/push-subscription", { endpoint });
  }

  // ── low-level helpers ────────────────────────────────────────────

  private async get<T>(path: string): Promise<T> {
    const res = await this.fetchImpl(`${this.baseURL}/api/v1${path}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    return this.handle<T>(res);
  }

  private async post<T = void>(path: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "Idempotency-Key": uuidv4(),
    };
    const csrf = this.cookieJar.get("sms_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await this.fetchImpl(`${this.baseURL}/api/v1${path}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
    return this.handle<T>(res);
  }

  // DELETE with an optional JSON body. Threads CSRF the same way as
  // post. Most DELETE endpoints take an empty body, but the
  // push-unsubscribe path uses the body to identify the endpoint.
  private async delete<T = void>(path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const csrf = this.cookieJar.get("sms_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await this.fetchImpl(`${this.baseURL}/api/v1${path}`, {
      method: "DELETE",
      credentials: "include",
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return this.handle<T>(res);
  }

  private async handle<T>(res: Response): Promise<T> {
    if (res.ok) {
      if (res.status === 204) return undefined as T;
      const text = await res.text();
      if (!text) return undefined as T;
      return JSON.parse(text) as T;
    }
    let problem: ProblemDetails = {
      status: res.status,
      title: res.statusText,
    };
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("json")) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        // Body wasn't JSON after all — fall through with the stub.
      }
    }
    throw mapError(res, problem);
  }
}

/**
 * DeviceClient — bearer-token-authenticated client for the gate kiosk
 * flow. Separate from the user `Client` because:
 *
 *   - Auth scheme is `Authorization: Bearer <api_token>`, not session
 *     cookie. No CSRF, no Cookie jar.
 *   - The kiosk has no user → no /me, no /logout. Keeping the surface
 *     narrow prevents call sites from accidentally mixing the two
 *     auth modes.
 *
 * The Idempotency-Key header on POST /arrivals IS the server-side
 * client_uuid (the Go handler parses it directly from the header).
 * One UUIDv4 per call keeps captures idempotent under flaky 3G.
 */
export interface DeviceClientOptions {
  baseURL: string;
  apiToken: string;
  fetchImpl?: typeof fetch;
}

export class DeviceClient {
  private readonly baseURL: string;
  private readonly apiToken: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: DeviceClientOptions) {
    this.baseURL = opts.baseURL.replace(/\/$/, "");
    this.apiToken = opts.apiToken;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * POST /api/v1/arrivals — records a single arrival.
   *
   * `opts.idempotencyKey` should be stable for the *logical* arrival.
   * The gate-app outbox supplies it at enqueue time so that a retried
   * delivery (offline → network back → flush) hits the server's
   * dedupe window and returns `deduped: true` instead of recording
   * the same arrival twice. When omitted, a fresh UUIDv4 is generated
   * (fine for one-shot online captures).
   */
  async recordArrival(
    req: RecordArrivalRequest,
    opts: { idempotencyKey?: string } = {},
  ): Promise<RecordArrivalResponse> {
    const res = await this.fetchImpl(`${this.baseURL}/api/v1/arrivals`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${this.apiToken}`,
        "Idempotency-Key": opts.idempotencyKey ?? uuidv4(),
      },
      body: JSON.stringify(req),
    });
    if (res.ok) {
      if (res.status === 204) return undefined as unknown as RecordArrivalResponse;
      return (await res.json()) as RecordArrivalResponse;
    }
    let problem: ProblemDetails = { status: res.status, title: res.statusText };
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("json")) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* fall through */
      }
    }
    throw mapError(res, problem);
  }
}

/** Re-export so the gate-app can mint stable Idempotency-Keys at enqueue. */
export function newIdempotencyKey(): string {
  return uuidv4();
}

function mapError(res: Response, problem: ProblemDetails): ApiError {
  switch (res.status) {
    case 400:
      return new ValidationError(problem);
    case 401:
      if (
        typeof problem.detail === "string" &&
        problem.detail.toLowerCase().includes("totp_code")
      ) {
        return new TOTPRequiredError(problem);
      }
      return new UnauthorizedError(problem);
    case 409:
      return new ConflictError(problem);
    case 429: {
      const ra = res.headers.get("retry-after");
      const secs = ra ? Number.parseInt(ra, 10) : undefined;
      return new RateLimitError(problem, Number.isFinite(secs) ? secs : undefined);
    }
    default:
      return new ApiError(res.status, problem);
  }
}

/**
 * UUIDv4 — crypto.randomUUID when available (browser + Node 19+),
 * fallback to Math.random for the rare environment without it
 * (idempotency keys don't need to be cryptographically random — they
 * just need to be unique per call).
 */
function uuidv4(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
